import './RateDetailsDrawer.less';

import { HistoryOutlined } from '@ant-design/icons';
import Icon from '@ant-design/icons';
import { Button, Card, Col, Drawer, Form, Row, Tooltip } from 'antd';
import { NumericDictionary } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import {
  formatCurrency,
  getfuelExpiryString,
  timeAgo,
} from '../../library/util';
import { Carrier } from '../../models/Carrier';
import { Rate } from '../../models/Rate';
import { ShipType } from '../../models/ShipType';
import rateService from '../../services/rateService';
import RateMap from '../common/RateMap';
import FuelSurchargeSvg from '../icons/FuelSurchargeSvg';
import RateHistoryModal from '../modals/RateHistoryModal';

interface IProps {
  keyedCarriers: NumericDictionary<Carrier>;
  onClose: () => void;
  rate: Rate;
  shipTypes: ShipType[];
}

const RateDetailsDrawer: React.FC<IProps> = ({
  keyedCarriers,
  onClose,
  rate,
  shipTypes,
}) => {
  const [form] = Form.useForm();
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | undefined>();

  const { data: hasHistory } = useQuery(
    ['rateHistoryExists', rate.id],
    () => rateService.hasHistory(rate.id),
    { enabled: !!rate.id }
  );

  const closeDrawer = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  useEffect(() => {
    if (rate) {
      form.setFieldsValue(rate);
      if (keyedCarriers && rate.carrierId) {
        const newCarrier = keyedCarriers[rate.carrierId];
        setSelectedCarrier(newCarrier);
      }
    }
  }, [form, rate, keyedCarriers]);

  const onFieldsChange = useCallback(
    (changedFields: any) => {
      if (changedFields.carrierId) {
        const newCarrier = keyedCarriers[changedFields.carrierId.toString()];
        setSelectedCarrier(newCarrier);
      }
    },
    [keyedCarriers]
  );

  const dataEntry = (
    label: React.ReactNode,
    value: any,
    span = 12,
    useHTML = false
  ) => {
    return (
      <Col span={span}>
        <Form.Item label={typeof label === 'string' ? <b>{label}</b> : label}>
          {useHTML ? (
            <div dangerouslySetInnerHTML={{ __html: value }} />
          ) : (
            value
          )}
        </Form.Item>
      </Col>
    );
  };

  const originShipTypeName = shipTypes.find(
    (st) => st.id === rate.shipTypeId
  )?.name;
  const destShipTypeName = shipTypes.find(
    (st) => st.id === rate.destinationShipTypeId
  )?.name;

  // collect the pricing - only where there actually is pricing
  type vehiclePrice = {
    id: number;
    name: String;
    basePrice: number | null;
    fuelSurcharge: number;
    fullPrice: number | null;
    updatedAt?: Date;
  };
  let prices: vehiclePrice[] = [];
  if (selectedCarrier && selectedCarrier.classes) {
    selectedCarrier.classes.forEach((carrierClass) => {
      let matchingRate = rate.classRate[carrierClass.id];
      if (matchingRate) {
        let newVP: vehiclePrice = {
          basePrice: matchingRate.value,
          fuelSurcharge: 0,
          fullPrice: matchingRate.value,
          id: carrierClass.id,
          name: carrierClass.name,
          updatedAt: matchingRate.updatedAt,
        };
        const surcharge = selectedCarrier.option?.fuelSurcharge || 0;
        if (newVP.basePrice) {
          if (!rate.skipFuelSurcharge) {
            newVP.fuelSurcharge = newVP.basePrice * (surcharge / 100);
            newVP.fullPrice = newVP.basePrice + newVP.fuelSurcharge;
          }
        }
        prices.push(newVP);
      }
    });
  }

  return (
    <Drawer
      open
      className="rate-details-drawer"
      placement="right"
      title={'Rate Details: ' + rate.id}
      onClose={closeDrawer}
    >
      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={onFieldsChange}
      >
        <div className="rate-details-drawer__section-header">OVERVIEW</div>
        <Row gutter={12}>
          <Col span={24} style={{ marginBottom: 8 }}>
            <RateMap height="270px" rate={rate} />
          </Col>
        </Row>
        <Row gutter={12}>
          {dataEntry('Carrier', selectedCarrier?.name)}
          {dataEntry('Mode', rate.modeName)}
          {dataEntry(
            'Origin',
            <>
              {rate.origin}
              {originShipTypeName && (
                <>
                  <br />
                  <span style={{ fontSize: '0.9em', opacity: 0.85 }}>
                    {originShipTypeName}
                  </span>
                </>
              )}
            </>
          )}
          {dataEntry(
            'Destination',
            <>
              {rate.destination}
              {destShipTypeName && (
                <>
                  <br />
                  <span style={{ fontSize: '0.9em', opacity: 0.85 }}>
                    {destShipTypeName}
                  </span>
                </>
              )}
            </>
          )}
        </Row>
        <Row gutter={12}>
          {dataEntry('Distance', `${Math.floor(rate.distance || 0)} km`)}
          {dataEntry('Transit Time', `${rate.transitDays} days`)}
        </Row>
        <Row gutter={12}>
          {dataEntry(
            'Rate Overview Last Updated',
            rate.updatedAt
              ? new Date(rate.updatedAt).toLocaleDateString('en-US')
              : '—',
            24
          )}
        </Row>
        {selectedCarrier &&
          selectedCarrier.option &&
          !rate.skipFuelSurcharge &&
          selectedCarrier.option?.fuelSurcharge > 0 && (
            <>
              <div
                className="rate-details-drawer__section-header"
                style={{ marginTop: 8 }}
              >
                FUEL SURCHARGE
              </div>
              <Row gutter={12}>
                {dataEntry(
                  <Icon component={FuelSurchargeSvg} />,
                  `${selectedCarrier.option?.fuelSurcharge || 0}%`,
                  6
                )}
                {dataEntry(
                  'Last Updated',
                  selectedCarrier.last_surcharge_change
                    ? timeAgo(selectedCarrier.last_surcharge_change)
                    : '—',
                  9
                )}
                {dataEntry(
                  'Expiry',
                  selectedCarrier.option?.fuelSurchargeExpiry
                    ? (() => {
                        const expiryData = getfuelExpiryString(
                          String(selectedCarrier?.option?.fuelSurchargeExpiry)
                        );
                        return (
                          <span
                            style={
                              expiryData.color
                                ? { color: expiryData.color }
                                : undefined
                            }
                          >
                            {expiryData.date}
                          </span>
                        );
                      })()
                    : '—',
                  9
                )}
              </Row>
            </>
          )}
        {prices && prices.length > 0 && (
          <>
            <div
              className="rate-details-drawer__section-header"
              style={{
                alignItems: 'end',
                display: 'flex',
                gap: 8,
                marginTop: 8,
              }}
            >
              RATE CLASSES
              {hasHistory && (
                <Button
                  icon={<HistoryOutlined />}
                  size="small"
                  type="link"
                  onClick={(): void => setHistoryModalOpen(true)}
                >
                  View History
                </Button>
              )}
            </div>
            <Row className="rate-details-drawer__price-cards" gutter={12}>
              {prices.map((nextPrice) => (
                <Col key={nextPrice.id} span={12}>
                  <Card
                    title={
                      <Tooltip title={nextPrice.name}>
                        <span>{nextPrice.name}</span>
                      </Tooltip>
                    }
                  >
                    <table className="rate-details-drawer__price-table">
                      <tbody>
                        <tr>
                          <th>Base:</th>
                          <td>{formatCurrency(nextPrice.basePrice)}</td>
                        </tr>
                        <tr>
                          <th>Fuel:</th>
                          <td>{formatCurrency(nextPrice.fuelSurcharge)}</td>
                        </tr>
                        <tr>
                          <th>Total:</th>
                          <td>{formatCurrency(nextPrice.fullPrice)}</td>
                        </tr>
                        <tr>
                          <th>Cost/KM:</th>
                          <td>
                            {rate.distance &&
                            rate.distance > 0 &&
                            nextPrice.basePrice != null
                              ? `$${(
                                  nextPrice.basePrice / rate.distance
                                ).toLocaleString('en-CA', {
                                  maximumFractionDigits: 3,
                                  minimumFractionDigits: 3,
                                })}`
                              : '—'}
                          </td>
                        </tr>
                        {nextPrice.updatedAt && (
                          <tr>
                            <th>Date:</th>
                            <td>
                              {new Date(nextPrice.updatedAt).toLocaleDateString(
                                'en-US'
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}
      </Form>
      <RateHistoryModal
        carrierClasses={selectedCarrier?.classes}
        modes={selectedCarrier?.modes}
        open={historyModalOpen}
        rateId={rate.id}
        shipTypes={shipTypes}
        onClose={(): void => setHistoryModalOpen(false)}
      />
    </Drawer>
  );
};

export default RateDetailsDrawer;
